import React from 'react';
import * as JsSearch from 'js-search';
import { createSlice } from '@reduxjs/toolkit';
import apiClient from '@miroculus/api-client';
import navigationService from '../../navigation/navigationService';
import { PROTOCOL_SOLVED_STATUS, PROTOCOL_SOLVING_STATUS } from '../../constants';
import { teamUrlWithVisibility, saveProtocolUrl } from '../../constants/routes';
import { toast } from 'react-toastify';
import { protocolTimeSelector, getProtocolValidationErrors, currentProtocolSelector, } from './selectors';
import socketClient from '../../socketClient';
import { showModal, hideModal } from '../ui/layout';
import { ErrorModalContent } from '../../components';
const search = new JsSearch.Search('id');
search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
search.addIndex('name');
search.addIndex(['type', 'label']);
search.addIndex(['cartridge', 'label']);
export const initialProtocols = [];
const initialState = {
    miroLayoutId: 0,
    selectedProtocolType: null,
    protocols: initialProtocols,
    filteredProtocols: initialProtocols,
    searchText: '',
    currentProtocol: {
        solution: null,
        graph: {
            actions: [],
            actionModules: [],
            edges: {},
        },
        library: {
            actionModules: [],
            reagents: [],
        },
        id: undefined,
        name: '',
        description: '',
        type: null,
        cartridge: null,
        time: 0,
        author: {},
        solutionStatus: '',
        validationErrors: [],
        errorCycleIndex: -1,
        warningCycleIndex: -1,
        team: null,
        visibility: null,
        template: null,
    },
    loading: false,
    protocolTypes: [],
    cartridges: [],
    saving: false,
    sendingProtocol: false,
    showingDevices: false,
    showingSolution: false,
    loadingSolution: false,
};
const protocolsSlice = createSlice({
    name: 'protocols',
    initialState,
    reducers: {
        // Top-level state updates
        updateProtocolsFilters: (state) => {
            state.filteredProtocols = (state.searchText
                ? search.search(state.searchText)
                : state.protocols).filter((protocol) => {
                return state.selectedProtocolType
                    ? protocol.type &&
                        protocol.type.id === state.selectedProtocolType.value
                    : true;
            });
        },
        updateSelectedProtocolType: (state, action) => {
            state.selectedProtocolType = action.payload;
        },
        _updateProtocols: (state, action) => {
            // Side effect: update search index (kept for backward compatibility)
            search.addDocuments(state.protocols);
            state.protocols = action.payload;
        },
        updateLoading: (state, action) => {
            state.loading = action.payload;
        },
        updateProtocolTypes: (state, action) => {
            state.protocolTypes = action.payload;
        },
        updateSaving: (state, action) => {
            state.saving = action.payload;
        },
        updateSendingProtocol: (state, action) => {
            state.sendingProtocol = action.payload;
        },
        updateShowingDevices: (state, action) => {
            state.showingDevices = action.payload;
        },
        updateMiroLayoutId: (state, action) => {
            state.miroLayoutId = action.payload;
        },
        updateReagentProfiles: (state, action) => {
            state.reagentProfiles = action.payload;
        },
        updateShowingSolution: (state, action) => {
            state.showingSolution = action.payload;
        },
        updateLoadingSolution: (state, action) => {
            state.loadingSolution = action.payload;
        },
        // CurrentProtocol nested state updates
        updateProtocolName: (state, action) => {
            state.currentProtocol.name = action.payload;
        },
        updateProtocolSolution: (state, action) => {
            state.currentProtocol.solution = action.payload;
        },
        updateProtocolType: (state, action) => {
            state.currentProtocol.type = action.payload;
        },
        updateProtocolDescription: (state, action) => {
            state.currentProtocol.description = action.payload;
        },
        updateProtocolDuration: (state, action) => {
            state.currentProtocol.time = action.payload;
        },
        updateProtocolPermissions: (state, action) => {
            state.currentProtocol.$permissions = action.payload;
        },
        updateProtocolCreatedAt: (state, action) => {
            state.currentProtocol.createdAt = action.payload;
        },
        updateProtocolCartridge: (state, action) => {
            state.currentProtocol.cartridge = action.payload;
        },
        updateProtocolId: (state, action) => {
            state.currentProtocol.id = action.payload;
        },
        updateProtocolGraph: (state, action) => {
            state.currentProtocol.graph = action.payload || { actions: [], actionModules: [], edges: {} };
        },
        updateProtocolAuthor: (state, action) => {
            state.currentProtocol.author = action.payload;
        },
        updateProtocolSolutionStatus: (state, action) => {
            state.currentProtocol.solutionStatus = action.payload;
        },
        updateProtocolLibrary: (state, action) => {
            state.currentProtocol.library = action.payload;
        },
        updateProtocolValidationErrors: (state, action) => {
            state.currentProtocol.validationErrors = action.payload;
        },
        updateProtocolModified: (state, action) => {
            state.currentProtocol.modified = action.payload;
        },
        updateProtocolTeam: (state, action) => {
            state.currentProtocol.team = action.payload;
        },
        updateProtocolVisibility: (state, action) => {
            state.currentProtocol.visibility = action.payload;
        },
        updateProtocolTemplate: (state, action) => {
            state.currentProtocol.template = action.payload;
        },
        updateErrorCycleIndex: (state, action) => {
            state.currentProtocol.errorCycleIndex = action.payload;
        },
        updateWarningCycleIndex: (state, action) => {
            state.currentProtocol.warningCycleIndex = action.payload;
        },
        resetCurrentProtocol: (state) => {
            state.currentProtocol = { ...initialState.currentProtocol };
        },
    },
});
// Export actions
export const { updateProtocolsFilters, updateSelectedProtocolType, _updateProtocols, updateLoading, updateProtocolTypes, updateSaving, updateSendingProtocol, updateShowingDevices, updateMiroLayoutId, updateReagentProfiles, updateShowingSolution, updateLoadingSolution, updateProtocolName, updateProtocolSolution, updateProtocolType, updateProtocolDescription, updateProtocolDuration, updateProtocolPermissions, updateProtocolCreatedAt, updateProtocolCartridge, updateProtocolId, updateProtocolGraph, updateProtocolAuthor, updateProtocolSolutionStatus, updateProtocolLibrary, updateProtocolValidationErrors, updateProtocolModified, updateProtocolTeam, updateProtocolVisibility, updateProtocolTemplate, updateErrorCycleIndex, updateWarningCycleIndex, resetCurrentProtocol, } = protocolsSlice.actions;
// Thunk Actions (kept as regular thunks due to cross-module dispatches and side effects)
export const loadProtocolSolution = (id) => async (dispatch) => {
    dispatch(updateLoadingSolution(true));
    try {
        const { body } = await apiClient.get(`/protocol/solutions/${id}`);
        dispatch(updateProtocolSolution(JSON.parse(body.solution)));
        dispatch(updateShowingSolution(true));
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong loading the protocol solution');
    }
    dispatch(updateLoadingSolution(false));
};
export const sendProtocolToDevice = (protocol, deviceId) => async (dispatch) => {
    dispatch(updateSendingProtocol(true));
    try {
        await apiClient.post(`/protocols/send/${protocol}/${deviceId}`);
        toast.success('The protocol was sent successfully');
    }
    catch (e) {
        console.error(e);
        toast.error('There was a problem sending the protocol to the device');
    }
    dispatch(updateSendingProtocol(false));
};
export const unsubscribeFromProtocols = (teamId, visibility) => async () => {
    await socketClient.unsubscribeFromProtocols(teamId, visibility);
};
export const saveReagents = (teamId) => async (dispatch, getState) => {
    const { protocols: { currentProtocol: { library }, }, } = getState();
    // Just save new reagents
    const reagentsToSave = library.reagents.filter((r) => r.new);
    try {
        await Promise.all(reagentsToSave.map((r) => apiClient.post('/reagents', {
            className: r.name,
            team: teamId,
        })));
        // set current protocol library reagents as not-new
        dispatch(updateProtocolLibrary({
            ...library,
            reagents: [...library.reagents.map((r) => ({ ...r, new: undefined }))],
        }));
    }
    catch (e) {
        console.error(e);
        toast.error('There was a problem trying to save the new reagents');
    }
};
export const saveProtocol = (values) => async (dispatch, getState) => {
    const { protocols: { currentProtocol: { id }, }, } = getState();
    await dispatch(updateProtocolName(values.name));
    await dispatch(updateProtocolType(values.type));
    await dispatch(updateProtocolDescription(values.description));
    await dispatch(updateProtocolVisibility(values.visibility));
    await dispatch(updateProtocolTeam(values.teamId));
    await dispatch(saveReagents(values.teamId));
    // check if protocols.currentProtocol.id exists,
    // if it exists, update the protocol,
    // if it doesn't, create a new protocol and add it to the global state
    await dispatch((id ? updateProtocolAction(values) : addProtocol(values)));
    toast.success(`The ${values.name} protocol was stored successfully`);
    navigationService.push(teamUrlWithVisibility(values.teamId, values.visibility));
};
export const checkProtocolToSave = (teamId) => (dispatch, getState) => {
    const state = getState();
    const { id } = currentProtocolSelector(state);
    const validationErrors = getProtocolValidationErrors(state);
    const goOn = () => {
        navigationService.push(saveProtocolUrl(teamId, id));
    };
    const errorCount = validationErrors.length;
    if (errorCount > 0) {
        dispatch(showModal(React.createElement(ErrorModalContent, {
            errorCount,
            onCancel: () => {
                dispatch(hideModal());
                goOn();
            },
            onConfirm: () => {
                dispatch(hideModal());
                dispatch(updateErrorCycleIndex(0));
            },
        })));
    }
    else {
        goOn();
    }
};
export const loadProtocolTypes = () => async (dispatch) => {
    dispatch(updateLoading(true));
    const { body } = await apiClient.get('/protocol/options');
    const protocolTypes = body.map((type) => ({
        label: type.label,
        value: type.id,
    }));
    dispatch(updateProtocolTypes(protocolTypes));
    dispatch(updateLoading(false));
};
export const loadMiroLayoutId = () => async (dispatch) => {
    try {
        const { body } = await apiClient.get('/electrode-layouts');
        const miroLayoutId = body.find((c) => c.name.toLowerCase() === 'miro')?.id;
        dispatch(updateMiroLayoutId(miroLayoutId || 0));
    }
    catch (e) {
        console.error(e);
        toast.error('There was a problem loading the available layouts.');
    }
};
export const loadSupportedProtocols = (organization) => async (dispatch) => {
    try {
        const { body } = await apiClient.get('/protocols/supported', {
            organization,
        });
        dispatch(updateProtocols(body));
    }
    catch (e) {
        console.error(e);
        toast.error('There was a problem loading the supported protocols.');
        dispatch(updateProtocols([]));
    }
};
export const loadProtocols = (teamId, visibility) => async (dispatch) => {
    dispatch(updateLoading(true));
    try {
        const protocols = await socketClient.subscribeToProtocols(teamId, visibility);
        await dispatch(updateProtocols(protocols));
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong loading the protocols');
    }
    finally {
        dispatch(updateLoading(false));
    }
};
export const deleteProtocol = (id) => async (dispatch, getState) => {
    const { protocols: { protocols }, } = getState();
    const index = protocols.map((p) => p.id).indexOf(id);
    const protocol = protocols[index];
    try {
        await apiClient.del(`/protocols/${id}`);
        await dispatch(_updateProtocols([
            ...protocols.slice(0, index),
            ...protocols.slice(index + 1),
        ]));
        toast.success(`The ${protocol.name} protocol was deleted successfully`);
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong trying to delete the protocol');
    }
};
export const setProtocolAsCurrent = (id) => async (dispatch) => {
    const { body } = await apiClient.get(`/protocols/${id}`);
    dispatch(updateProtocolAuthor(body.author));
    dispatch(updateProtocolName(body.name));
    dispatch(updateProtocolType(body.type.id));
    dispatch(updateProtocolDescription(body.description));
    dispatch(updateProtocolCartridge(body.cartridge));
    dispatch(updateProtocolId(body.id));
    dispatch(updateProtocolDuration(body.time));
    dispatch(updateProtocolCreatedAt(body.createdAt));
    dispatch(updateProtocolGraph(body.protocol));
    dispatch(prepareProtocol(body.protocol));
    dispatch(updateProtocolSolutionStatus(body.solutionStatus));
    dispatch(updateProtocolTeam(body.team));
    dispatch(updateProtocolVisibility(body.visibility));
    dispatch(updateProtocolPermissions(body.$permissions));
    dispatch(updateProtocolTemplate(body.template));
};
export const loadProtocolToCopy = (protocolId) => async (dispatch, getState) => {
    const { auth: { user }, } = getState();
    dispatch(updateProtocolId(undefined));
    dispatch(updateProtocolAuthor(user));
    const { body } = await apiClient.get(`/protocols/${protocolId}`);
    dispatch(updateProtocolType(body.type.id));
    dispatch(updateProtocolName(`Copy of ${body.name}`));
    dispatch(updateProtocolDescription(body.description));
    dispatch(updateProtocolCartridge(body.cartridge));
    dispatch(updateProtocolGraph(body.protocol));
    dispatch(updateProtocolVisibility(body.visibility));
};
// API calls
const addProtocol = ({ name, description, type, visibility, teamId, protocolCopyFromId }) => async (dispatch, getState) => {
    const { protocols: { miroLayoutId, protocols, currentProtocol: { graph }, }, auth: { user }, } = getState();
    const newProtocol = {
        name,
        description,
        type,
        time: protocolTimeSelector(getState()),
        cartridge: miroLayoutId,
        author: user,
        protocol: graph,
        steps: [],
        visibility,
        team: teamId,
    };
    dispatch(updateSaving(true));
    const { body } = await apiClient.post(protocolCopyFromId
        ? `/protocols/${protocolCopyFromId}/copy/${teamId}`
        : '/protocols', newProtocol);
    dispatch(_updateProtocols([
        ...protocols,
        {
            ...newProtocol,
            id: body.id,
            protocol: graph,
        },
    ]));
    dispatch(updateProtocolId(body.id));
    dispatch(updateProtocolSolutionStatus(body.solutionStatus));
    dispatch(updateProtocolAuthor(user));
    dispatch(updateSaving(false));
};
const updateProtocolAction = ({ name, description, type, visibility, teamId: team }) => async (dispatch, getState) => {
    const { protocols: { currentProtocol: { id, cartridge, graph }, }, auth: { user }, } = getState();
    const time = protocolTimeSelector(getState());
    const newProtocol = {
        id,
        name,
        description,
        time,
        cartridge,
        type,
        author: user.email,
        protocol: graph,
        visibility,
        team,
    };
    dispatch(updateSaving(true));
    const { body } = await apiClient.post(`/protocols/${id}`, {
        ...newProtocol,
    });
    dispatch(updateProtocolSolutionStatus(body.solutionStatus));
    dispatch(updateSaving(false));
};
const parseActionModule = ({ title, graph = {}, $permissions = {}, ...am }) => ({
    ...am,
    name: title,
    actions: graph.actions || [],
    canEdit: $permissions.canEdit,
});
export const addActionModuleToLibrary = (actionModule) => async (dispatch, getState) => {
    const state = getState();
    const { library } = state.protocols.currentProtocol;
    const currentProtocol = currentProtocolSelector(state);
    // @TODO: allow to add action modules when creating a new protocol
    if (!currentProtocol?.team)
        return;
    const { body } = await apiClient.post('/action-modules', {
        title: actionModule.name,
        graph: { actions: actionModule.actions },
        team: currentProtocol.team,
    });
    dispatch(updateProtocolLibrary({
        ...library,
        actionModules: [...library.actionModules, parseActionModule(body)],
    }));
    toast.success('Action module saved in library');
};
export const updateActionModuleInLibrary = (id, actionModule) => (dispatch, getState) => {
    const state = getState();
    const { library } = state.protocols.currentProtocol;
    apiClient.post(`/action-modules/${id}`, {
        graph: { actions: actionModule.actions },
    });
    dispatch(updateProtocolLibrary({
        ...library,
        actionModules: library.actionModules.map((am) => am.id === id ? { ...am, actions: actionModule.actions } : am),
    }));
};
export const editActionModule = (actionModule) => (dispatch, getState) => {
    apiClient.post(`/action-modules/${actionModule.id}`, {
        title: actionModule.name,
    });
    const { library } = getState().protocols.currentProtocol;
    dispatch(updateProtocolLibrary({
        ...library,
        actionModules: library.actionModules.map((am) => am.id === actionModule.id ? actionModule : am),
    }));
};
export const deleteActionModule = (index) => (dispatch, getState) => {
    const { library } = getState().protocols.currentProtocol;
    const actionModule = library.actionModules[index];
    apiClient.del(`/action-modules/${actionModule.id}`);
    dispatch(updateProtocolLibrary({
        ...library,
        actionModules: library.actionModules.filter((_, i) => i !== index),
    }));
};
const parseReagent = ({ className, $permissions = {}, ...reagent }) => ({
    name: className,
    canEdit: $permissions.canEdit,
    ...reagent,
});
export const addReagentToLibrary = (reagentName) => async (dispatch, getState) => {
    const state = getState();
    const { library } = state.protocols.currentProtocol;
    // don't add another copy of the same reagent class
    if (library.reagents.find((r) => r.name === reagentName))
        return;
    dispatch(updateProtocolLibrary({
        ...library,
        // Mark created reagents as "new", these ones will be saved on the DB
        reagents: [
            ...library.reagents,
            { name: reagentName, canEdit: true, new: true },
        ],
    }));
};
export const removeReagentFromLibrary = (index) => (dispatch, getState) => {
    const { library } = getState().protocols.currentProtocol;
    const reagent = library.reagents[index];
    apiClient.del(`/reagents/${reagent.id}`);
    dispatch(updateProtocolLibrary({
        ...library,
        reagents: library.reagents.filter((_, i) => i !== index),
    }));
};
export const updateReagents = (reagents) => (dispatch, getState) => {
    const { library } = getState().protocols.currentProtocol;
    dispatch(updateProtocolLibrary({
        ...library,
        reagents,
    }));
};
export const prepareProtocol = (protocolGraph) => async (dispatch, getState) => {
    let errors = [];
    let updatedGraph = null;
    const state = getState();
    const currentProtocol = state?.protocols?.currentProtocol;
    if (!currentProtocol) {
        console.warn('prepareProtocol called before protocols state initialized');
        return;
    }
    try {
        const { body } = await socketClient.prepareProtocol(protocolGraph, currentProtocol.id);
        errors = body.errors;
        updatedGraph = body.graph;
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong trying to validate the protocol');
        return;
    }
    dispatch(updateProtocolGraph(updatedGraph));
    dispatch(updateProtocolValidationErrors(errors));
};
export const loadReagentProfiles = () => async (dispatch) => {
    const { body } = await apiClient.get('/reagent-profiles');
    dispatch(updateReagentProfiles(body));
};
export const loadLibrary = () => async (dispatch, getState) => {
    const currentProtocol = currentProtocolSelector(getState());
    const teamId = currentProtocol?.team || undefined;
    const { body: actionModules } = await apiClient.get('/action-modules', {
        teamId,
    });
    const { body: reagents } = await apiClient.get('/reagents', { teamId });
    dispatch(updateProtocolLibrary({
        actionModules: actionModules.map(parseActionModule),
        reagents: reagents.map(parseReagent),
    }));
};
export const updateProtocols = (protocols) => (dispatch, getState) => {
    const oldProtocols = getState().protocols.protocols;
    for (const protocol of protocols) {
        // Show notification if a solving protocol is solved!
        if (protocol.solutionStatus === PROTOCOL_SOLVED_STATUS &&
            oldProtocols.find((p) => p.id === protocol.id)?.solutionStatus ===
                PROTOCOL_SOLVING_STATUS) {
            toast.success(`The ${protocol.name} protocol has been solved`);
        }
    }
    dispatch(_updateProtocols(protocols));
    dispatch(updateProtocolsFilters());
};
export const loadRecentProtocols = () => async (dispatch) => {
    dispatch(updateLoading(true));
    try {
        const { body } = await apiClient.get('/protocols/recent');
        dispatch(updateProtocols(body));
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong loading recent protocols');
    }
    finally {
        dispatch(updateLoading(false));
    }
};
export const loadDrafts = () => async (dispatch) => {
    dispatch(updateLoading(true));
    try {
        const { body } = await apiClient.get('/protocols/drafts');
        dispatch(updateProtocols(body));
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong loading drafts');
    }
    finally {
        dispatch(updateLoading(false));
    }
};
// Export reducer as default
export default protocolsSlice.reducer;
